// Admin-only dialog to manage the authorized medication catalog for a patient.
// Catalog is shared across all programs of the same patient (keyed by patient_name).

import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { VIAS, INDICATIONS } from "@/lib/meds";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Pencil, Trash2, Plus, ShieldCheck, Loader2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { exportMedCatalogPDF } from "@/lib/meds-pdf";

const empty = {
  name: "",
  default_via: "Oral",
  default_dose: "",
  default_indication: "rx",
  notes: "",
  schedule_times_str: "",       // comma-separated HH:MM list (UI buffer)
  min_interval_hours: "",       // string in UI to allow empty
};

export default function MedicationCatalogDialog({
  open,
  onOpenChange,
  patientName,
  meds,
  reload,
}) {
  const [draft, setDraft] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const reset = useCallback(() => {
    setDraft(empty);
    setEditingId(null);
  }, []);

  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const save = async () => {
    const name = draft.name.trim();
    if (!name) {
      toast.error("Escribe el nombre del medicamento");
      return;
    }
    if (!patientName?.trim()) {
      toast.error("Captura el nombre del paciente antes de autorizar medicamentos");
      return;
    }
    // Parse schedule_times (comma-separated HH:MM)
    let schedule = [];
    if (draft.default_indication === "rx" && draft.schedule_times_str.trim()) {
      const items = draft.schedule_times_str.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
      for (const s of items) {
        if (!/^\d{1,2}:\d{2}$/.test(s)) {
          toast.error(`Horario invalido: ${s}. Usa formato HH:MM (ej. 08:00)`);
          return;
        }
        const [h, m] = s.split(":").map(Number);
        if (h < 0 || h > 23 || m < 0 || m > 59) {
          toast.error(`Hora fuera de rango: ${s}`);
          return;
        }
        schedule.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
      }
      schedule = Array.from(new Set(schedule)).sort();
    }
    // Parse min_interval_hours (PRN)
    let interval = null;
    if (draft.default_indication === "prn" && draft.min_interval_hours !== "") {
      const v = Number(draft.min_interval_hours);
      if (!Number.isFinite(v) || v < 0 || v > 72) {
        toast.error("Intervalo invalido (0 a 72 horas)");
        return;
      }
      interval = v;
    }
    setSaving(true);
    try {
      const body = {
        name,
        default_via: draft.default_via,
        default_dose: draft.default_dose.trim(),
        default_indication: draft.default_indication,
        notes: draft.notes.trim(),
        schedule_times: schedule,
        min_interval_hours: interval,
      };
      if (editingId) {
        await api.put(`/patient-meds/${editingId}`, body);
        toast.success("Medicamento actualizado");
      } else {
        await api.post("/patient-meds", { ...body, patient_name: patientName.trim() });
        toast.success("Medicamento autorizado");
      }
      reset();
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setDraft({
      name: m.name,
      default_via: m.default_via || "Oral",
      default_dose: m.default_dose || "",
      default_indication: m.default_indication || "rx",
      notes: m.notes || "",
      schedule_times_str: (m.schedule_times || []).join(", "),
      min_interval_hours: m.min_interval_hours == null ? "" : String(m.min_interval_hours),
    });
  };

  const remove = async (m) => {
    if (!window.confirm(`Quitar "${m.name}" de los medicamentos autorizados?`)) return;
    try {
      await api.delete(`/patient-meds/${m.id}`);
      toast.success("Medicamento eliminado del catalogo");
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="med-catalog-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-[#1D4ED8]" />
            Catalogo de medicamentos autorizados
          </DialogTitle>
          <DialogDescription>
            Paciente: <strong>{patientName || "(sin nombre)"}</strong>. Solo coordinador puede modificar.
            Las enfermeras administraran unicamente desde esta lista.
          </DialogDescription>
        </DialogHeader>

        {/* Existing list */}
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs font-bold uppercase tracking-wide text-slate-500">
              Lista actual ({meds.length})
            </Label>
            {meds.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 rounded-lg border-blue-300 px-2.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                disabled={downloading}
                onClick={async () => {
                  setDownloading(true);
                  try {
                    await exportMedCatalogPDF(patientName, meds);
                    toast.success("Listado de medicamentos descargado");
                  } catch (err) {
                    console.error(err);
                    toast.error("No se pudo generar el PDF");
                  } finally {
                    setDownloading(false);
                  }
                }}
                data-testid="med-catalog-download-btn"
              >
                {downloading ? (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FileDown className="mr-1 h-3.5 w-3.5" />
                )}
                Descargar listado
              </Button>
            )}
          </div>
          {meds.length === 0 ? (
            <p
              className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-sm text-slate-500"
              data-testid="med-catalog-empty"
            >
              Aun no hay medicamentos autorizados para este paciente.
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="med-catalog-list">
              {meds.map((m) => (
                <li
                  key={m.id}
                  className="flex items-start justify-between gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2"
                  data-testid={`med-catalog-item-${m.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-bold text-slate-900">{m.name}</span>
                      {m.default_dose && (
                        <span className="text-xs font-semibold text-slate-600">{m.default_dose}</span>
                      )}
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                          m.default_indication === "prn"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {m.default_indication === "prn" ? "PRN" : "Rx"}
                      </span>
                      {m.default_indication === "rx" && (m.schedule_times?.length || 0) > 0 && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-700">
                          {m.schedule_times.join(" - ")}
                        </span>
                      )}
                      {m.default_indication === "prn" && m.min_interval_hours != null && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-700">
                          min c/{m.min_interval_hours}h
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {m.default_via || "-"}
                      {m.notes && ` - ${m.notes}`}
                    </div>
                  </div>
                  <div className="flex flex-shrink-0 gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => startEdit(m)}
                      data-testid={`med-catalog-edit-${m.id}`}
                    >
                      <Pencil className="h-4 w-4 text-slate-600" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 hover:bg-red-50"
                      onClick={() => remove(m)}
                      data-testid={`med-catalog-delete-${m.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add / edit form */}
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          <Label className="block text-xs font-bold uppercase tracking-wide text-slate-500">
            {editingId ? "Editar medicamento" : "Autorizar nuevo medicamento"}
          </Label>
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Nombre <span className="text-red-600">*</span></Label>
              <Input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Ej. Paracetamol"
                className="h-10 text-base"
                data-testid="med-catalog-input-name"
              />
            </div>
            <div>
              <Label className="text-xs">Dosis (gramaje)</Label>
              <Input
                value={draft.default_dose}
                onChange={(e) => setDraft({ ...draft, default_dose: e.target.value })}
                placeholder="500 mg / 1 amp"
                className="h-10 text-base"
                data-testid="med-catalog-input-dose"
              />
            </div>
            <div>
              <Label className="text-xs">Via</Label>
              <Select
                value={draft.default_via}
                onValueChange={(v) => setDraft({ ...draft, default_via: v })}
              >
                <SelectTrigger className="h-10 text-base" data-testid="med-catalog-input-via">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VIAS.map((v) => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Tipo de indicacion</Label>
              <Select
                value={draft.default_indication}
                onValueChange={(v) => setDraft({ ...draft, default_indication: v })}
              >
                <SelectTrigger className="h-10 text-base" data-testid="med-catalog-input-indication">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {INDICATIONS.map((i) => (
                    <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Notas / criterio de uso</Label>
            <Textarea
              rows={2}
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              placeholder="Ej. Si fiebre >=38 C, max c/6h"
              className="text-base"
              data-testid="med-catalog-input-notes"
            />
          </div>

          {draft.default_indication === "rx" && (
            <div data-testid="med-catalog-rx-block">
              <Label className="text-xs">Horarios programados <span className="text-slate-500">(HH:MM separados por coma)</span></Label>
              <Input
                value={draft.schedule_times_str}
                onChange={(e) => setDraft({ ...draft, schedule_times_str: e.target.value })}
                placeholder="08:00, 14:00, 20:00"
                inputMode="numeric"
                className="h-10 text-base tabular-nums"
                data-testid="med-catalog-input-schedule"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Genera alertas amarillas en el turno si una dosis programada pasa de hora sin registrar.
              </p>
            </div>
          )}

          {draft.default_indication === "prn" && (
            <div data-testid="med-catalog-prn-block">
              <Label className="text-xs">Intervalo minimo entre dosis <span className="text-slate-500">(horas)</span></Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="72"
                value={draft.min_interval_hours}
                onChange={(e) => setDraft({ ...draft, min_interval_hours: e.target.value })}
                placeholder="Ej. 4 (cada 4 horas minimo)"
                className="h-10 text-base"
                data-testid="med-catalog-input-interval"
              />
              <p className="mt-1 text-[11px] text-slate-500">
                Bloquea el registro de una nueva dosis hasta cumplirse este intervalo (calculado contra la ultima dosis en cualquier turno).
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <Button
              onClick={save}
              disabled={saving}
              className="flex-1 rounded-xl bg-[#1D4ED8] font-semibold hover:bg-[#1E40AF]"
              data-testid="med-catalog-save"
            >
              {saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</>
              ) : editingId ? (
                <>Actualizar</>
              ) : (
                <><Plus className="mr-2 h-4 w-4" /> Autorizar</>
              )}
            </Button>
            {editingId && (
              <Button
                variant="outline"
                onClick={reset}
                className="rounded-xl"
                data-testid="med-catalog-cancel"
              >
                Cancelar
              </Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full rounded-xl"
            data-testid="med-catalog-close"
          >
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
